import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import ImageSlideshow from './ImageSlideshow';
import { getSlideshowImages } from '../services/api';

const HeroSlideshow = ({ onUserCTAClick, onBusinessCTAClick }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';
    const [currentIndex, setCurrentIndex] = useState(0);
    const [manualMode, setManualMode] = useState(false);
    const [manualTimeout, setManualTimeout] = useState(null);
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [imagesLoaded, setImagesLoaded] = useState(0);
    const [totalImages, setTotalImages] = useState(0);
    const BASE_INTERVAL = 7000;

    // Preload images
    const preloadImages = (imageUrls) => {
        setTotalImages(imageUrls.length);
        return Promise.all(
            imageUrls.map(url => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => {
                        setImagesLoaded(prev => prev + 1);
                        resolve(url);
                    };
                    img.onerror = () => reject(url);
                    img.src = url;
                });
            })
        );
    };

    // Fetch and preload images
    useEffect(() => {
        const loadImages = async () => {
            setIsLoading(true);
            try {
                const fetchedImages = await getSlideshowImages(isMobile);
                await preloadImages(fetchedImages);
                setImages(fetchedImages);
            } catch (error) {
                console.error('Failed to load slideshow images:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadImages();
    }, [isMobile]);

    // Auto-advance slideshow
    useEffect(() => {
        if (images.length === 0 || manualMode) return;

        const timer = setInterval(() => {
            setCurrentIndex(current => (current + 1) % images.length);
        }, BASE_INTERVAL);

        return () => clearInterval(timer);
    }, [images.length, manualMode]);

    const handleManualNavigate = (direction) => {
        setManualMode(true);
        if (manualTimeout) clearTimeout(manualTimeout);

        setCurrentIndex(current => {
            if (direction === 'prev') {
                return current === 0 ? images.length - 1 : current - 1;
            } else {
                return (current + 1) % images.length;
            }
        });

        const timeout = setTimeout(() => setManualMode(false), 10000);
        setManualTimeout(timeout);
    };

    if (isLoading) {
        return (
            <Container 
                maxWidth="lg" 
                disableGutters 
                sx={{ 
                    position: 'relative',
                    my: { xs: 1, sm: 2 }
                }}
            >
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: '180px', sm: '380px' },
                    borderRadius: { xs: '14px', sm: '20px' },
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark ? 'rgba(19, 28, 46, 0.5)' : 'rgba(241, 245, 249, 0.7)',
                    border: `1px solid ${theme.palette.divider}`,
                }}>
                    <CircularProgress size={36} thickness={4} sx={{ color: theme.palette.primary.main }} />
                    {totalImages > 0 && (
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mt: 2, 
                                color: theme.palette.text.secondary,
                                fontWeight: 500
                            }}
                        >
                            Loading preview... {Math.round((imagesLoaded / totalImages) * 100)}%
                        </Typography>
                    )}
                </Box>
            </Container>
        );
    }

    return (
        <Container 
            maxWidth="lg" 
            disableGutters 
            sx={{ 
                position: 'relative',
                my: { xs: 1, sm: 2 },
            }}
        >
            <Box sx={{
                position: 'relative',
                width: '100%',
                height: { 
                    xs: '190px', 
                    sm: '380px' 
                },
                borderRadius: { 
                    xs: '14px', 
                    sm: '20px' 
                },
                boxShadow: isDark 
                    ? '0 20px 50px -10px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)' 
                    : '0 20px 50px -15px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(15, 23, 42, 0.06)',
                overflow: 'hidden'
            }}>
                <ImageSlideshow 
                    images={images}
                    currentIndex={currentIndex}
                    onManualNavigate={handleManualNavigate}
                />
                
                {/* Modern Pill Slide Indicators */}
                {images.length > 1 && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: { xs: 10, sm: 16 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.75, sm: 1 },
                        px: { xs: 1.25, sm: 1.5 },
                        py: { xs: 0.5, sm: 0.75 },
                        borderRadius: '20px',
                        backgroundColor: 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        zIndex: 3
                    }}>
                        {images.map((_, index) => {
                            const isActive = currentIndex === index;
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        width: isActive ? { xs: 18, sm: 24 } : { xs: 6, sm: 8 },
                                        height: { xs: 6, sm: 8 },
                                        borderRadius: '4px',
                                        backgroundColor: isActive 
                                            ? '#FFFFFF' 
                                            : 'rgba(255, 255, 255, 0.35)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: isActive ? '0 0 8px rgba(255, 255, 255, 0.6)' : 'none',
                                        '&:hover': {
                                            backgroundColor: '#FFFFFF',
                                            opacity: 0.9,
                                        }
                                    }}
                                    onClick={() => {
                                        setCurrentIndex(index);
                                        setManualMode(true);
                                        if (manualTimeout) clearTimeout(manualTimeout);
                                        const timeout = setTimeout(() => setManualMode(false), 10000);
                                        setManualTimeout(timeout);
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default HeroSlideshow;